import { useDialog } from 'muibox';
import { useSnackbar } from 'notistack';
import { useContext, useState } from 'react';
import { useQuery, useQueryClient } from 'react-query';
import { useParams } from 'react-router-dom';
import { SettingsContext } from '../store/SettingsContext';
import { UserContext } from '../store/UserContext';
import { handleHttpError } from '../utils/common';
import useAxios from './useAxios';
import { OnlyFansMassDMMessageScheduleDocumentWithColor } from './useMessageUsersMassDmList';
import useSubscriber from './useSubscriber';

export type MassDmScheduleListSortBy = '_id' | 'scheduleDate' | 'name' | 'colorId';

export type MassDmScheduleAdjustUnit = 'minutes' | 'hours' | 'days' | 'weeks';

const useMessageUsersMassDmScheduleList = (
    query: string,
    active: boolean | null,
    colorId: string | undefined,
    sortBy: MassDmScheduleListSortBy,
) => {
    const userContext = useContext(UserContext);
    const settingsContext = useContext(SettingsContext);
    const params = useParams();
    const dialog = useDialog();
    const axios = useAxios();
    const { enqueueSnackbar } = useSnackbar();
    const queryClient = useQueryClient();

    const { data: subscriber } = useSubscriber();

    const [bulkDeleteScheduledMessagesLoading, setBulkDeleteScheduledMessagesLoading] = useState(false);
    const [bulkDistributeScheduledMessagesLoading, setBulkDistributeScheduledMessagesLoading] = useState(false);
    const [bulkAdjustScheduledMessagesLoading, setBulkAdjustScheduledMessagesLoading] = useState(false);
    const [isUpdatingScheduledMessageStatus, setIsUpdatingScheduledMessageStatus] = useState(false);
    const [bulkSetMassDmScheduleInactiveStatusLoading, setBulkSetMassDmScheduleInactiveStatusLoading] = useState(false);

    // Fetch Scheduled Messages
    const fetchScheduledMessages = async (
        fetchActive: boolean | null = active,
    ): Promise<OnlyFansMassDMMessageScheduleDocumentWithColor[]> => {
        const url: string = `${settingsContext.routes.massDm.base}${params.userId}/schedule?${new URLSearchParams({
            query: query.trim(),
            active: typeof fetchActive === 'boolean' ? (fetchActive ? 'true' : 'false') : 'null',
            colorId: colorId ? colorId : '',
            sortBy: sortBy,
        })}`;

        return axios
            .get(url)
            .then(async response => {
                if (response.status === 200) {
                    return response.data;
                } else {
                    return Promise.reject(response.data);
                }
            })
            .catch(error => {
                console.error(error);

                enqueueSnackbar('Failed to fetch scheduled messages', { variant: 'error' });

                return [];
            });
    };

    const messageUsersScheduledSessions = useQuery(
        ['subscriberMassDmsSchedule', subscriber?._id, query, active, colorId, sortBy],
        () => fetchScheduledMessages(),
        {
            refetchOnWindowFocus: false,
            staleTime: 5000,
            enabled:
                userContext.jwtToken && settingsContext.apiKey && 'userId' in params && params.userId && subscriber && subscriber._id
                    ? true
                    : false,
        },
    );

    const bulkDeleteScheduledMessages = async (massDmIds: string[], active: boolean): Promise<{ success: boolean }> => {
        if (!userContext.jwtToken || !settingsContext.apiKey) {
            return { success: false };
        }

        const url: string = `${settingsContext.routes.massDm.base}${params.userId}/bulkDelete`;

        setBulkDeleteScheduledMessagesLoading(true);

        const data = await axios
            .delete(url, {
                data: {
                    massDmIds,
                    active,
                },
            })
            .then(response => response.data)
            .catch(error => {
                handleHttpError(error, dialog);

                return { success: false };
            })
            .finally(() => {
                setBulkDeleteScheduledMessagesLoading(false);
            });

        return data;
    };

    const bulkDistributeScheduledMessages = async (
        massDmIds: string[],
        offsetAmount: number,
        offsetUnit: string,
        startDate: Date,
    ): Promise<{ success: boolean }> => {
        if (!userContext.jwtToken || !settingsContext.apiKey) {
            return { success: false };
        }

        const url: string = `${settingsContext.routes.massDm.base}${params.userId}/bulkDistribute`;

        setBulkDistributeScheduledMessagesLoading(true);

        const data = await axios
            .post(url, {
                massDmIds,
                offsetAmount,
                offsetUnit,
                startDate,
            })
            .then(response => response.data)
            .catch(error => {
                handleHttpError(error, dialog);

                return { success: false };
            })
            .finally(() => {
                setBulkDistributeScheduledMessagesLoading(false);
            });

        return data;
    };

    const bulkAdjustScheduledMessages = async (
        massDmIds: string[],
        offsetAmount: number,
        offsetUnit: MassDmScheduleAdjustUnit,
    ): Promise<{ success: boolean; adjustedCount?: number }> => {
        if (!userContext.jwtToken || !settingsContext.apiKey) {
            return { success: false };
        }

        const url: string = `${settingsContext.routes.massDm.base}${params.userId}/bulkAdjust`;

        setBulkAdjustScheduledMessagesLoading(true);

        const data = await axios
            .post(url, {
                massDmIds,
                offsetAmount,
                offsetUnit,
            })
            .then(response => response.data)
            .catch(error => {
                handleHttpError(error, dialog);

                return { success: false };
            })
            .finally(() => {
                setBulkAdjustScheduledMessagesLoading(false);
            });

        return data;
    };

    const setMassDmScheduleInactiveStatus = async (massDmScheduleId: string, inactive: boolean): Promise<{ success: boolean }> => {
        if (!userContext.jwtToken || !settingsContext.apiKey) {
            return { success: false };
        }

        const url: string = `${settingsContext.routes.massDm.base}${params.userId}/schedule/${massDmScheduleId}/status`;

        setIsUpdatingScheduledMessageStatus(true);

        const data = await axios
            .patch(url, { inactive })
            .then(response => {
                queryClient.invalidateQueries(['subscriberMassDmsSchedule', subscriber?._id]);

                return response.data;
            })
            .catch(error => {
                handleHttpError(error, dialog);

                return { success: false };
            })
            .finally(() => {
                setIsUpdatingScheduledMessageStatus(false);
            });

        return data;
    };

    // Always fetch the active schedules regardless of the current list filter so the
    // bulk pause / resume dialog can offer both running and paused schedules.
    const fetchActiveScheduledMessagesForBulkPause = async (): Promise<OnlyFansMassDMMessageScheduleDocumentWithColor[]> => {
        return fetchScheduledMessages(true);
    };

    const bulkSetMassDmScheduleInactiveStatus = async (massDmIds: string[], inactive: boolean): Promise<{ success: boolean }> => {
        if (!userContext.jwtToken || !settingsContext.apiKey) {
            return { success: false };
        }

        const url: string = `${settingsContext.routes.massDm.base}${params.userId}/bulkStatus`;

        setBulkSetMassDmScheduleInactiveStatusLoading(true);

        const data = await axios
            .patch(url, {
                massDmIds,
                inactive,
            })
            .then(response => {
                queryClient.invalidateQueries(['subscriberMassDmsSchedule', subscriber?._id]);

                return response.data;
            })
            .catch(error => {
                handleHttpError(error, dialog);

                return { success: false };
            })
            .finally(() => {
                setBulkSetMassDmScheduleInactiveStatusLoading(false);
            });

        return data;
    };

    return {
        ...messageUsersScheduledSessions,
        bulkDeleteScheduledMessagesLoading,
        bulkDistributeScheduledMessagesLoading,
        bulkAdjustScheduledMessagesLoading,
        bulkDeleteScheduledMessages,
        bulkDistributeScheduledMessages,
        bulkAdjustScheduledMessages,
        setMassDmScheduleInactiveStatus,
        isUpdatingScheduledMessageStatus,
        bulkSetMassDmScheduleInactiveStatus,
        bulkSetMassDmScheduleInactiveStatusLoading,
        fetchActiveScheduledMessagesForBulkPause,
    };
};

export default useMessageUsersMassDmScheduleList;
